<template>
  <div class="navbar">
    <v-menu
      v-for="group in groups"
      :key="group.key"
      offset-y
      class="navbar-menu"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn text v-bind="attrs" v-on="on">
          <v-icon left>
            {{ group.icon }}
          </v-icon>
          {{ group.label }}
        </v-btn>
      </template>

      <v-list>
        <v-list-item
          v-for="link in group.links"
          :key="link.path"
          :to="link.path"
          :exact="link.exact"
          link
        >
          <v-list-item-title>{{ link.meta.label }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <v-btn
      v-if="hasPaidServiceAccess"
      text
      @click="openPaidDialog"
    >
      <v-icon left>attach_money</v-icon>
      Платные
    </v-btn>

    <v-dialog v-model="paidDialog" max-width="560px" persistent>
      <v-card>
        <v-card-title>
          Платные услуги
          <v-spacer></v-spacer>
          <v-btn icon @click="closePaidDialog">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-form>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="paidFilter.from"
                  label="С"
                  type="date"
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="paidFilter.to"
                  label="По"
                  type="date"
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-autocomplete
                  v-model="paidFilter.hospitalIds"
                  :items="hospitals"
                  :loading="hospitalsLoading"
                  label="Организации"
                  item-text="name"
                  item-value="id"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  clearable
                ></v-autocomplete>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <div class="paid-sync-actions">
            <v-btn
              color="primary"
              small
              :loading="syncingCatalogs"
              @click="syncMisCatalogs"
            >
              Обновить справочники
              <v-icon right>sync</v-icon>
            </v-btn>
            <v-btn
              color="primary"
              small
              :loading="syncingServices"
              @click="syncMisServices"
            >
              Обновить услуги
              <v-icon right>sync</v-icon>
            </v-btn>
          </div>
          <v-spacer></v-spacer>
          <v-btn small @click="closePaidDialog">Отмена</v-btn>
          <v-btn
            color="green"
            small
            class="white--text"
            :loading="exporting"
            :disabled="!canExport"
            @click="exportPaidReport"
          >
            Экспорт
            <v-icon right dark>cloud_download</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-spacer></v-spacer>
    <v-menu offset-y>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          v-bind="attrs"
          color="primary"
          fab
          dark
          small
          depressed
          v-on="on"
        >
          <v-icon>account_circle</v-icon>
        </v-btn>
      </template>

      <v-list>
        <v-list-item two-line>
          <v-list-item-content>
            <v-list-item-title>{{ storage.getName() }}</v-list-item-title>
            <v-list-item-subtitle>{{
              storage.getUserName()
            }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>
      <v-divider />
      <v-list dense>
        <v-list-item-group color="primary">
          <v-list-item @click="profile">
            <v-list-item-icon>
              <v-icon>person</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Профиль</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item @click="storage.clear()">
            <v-list-item-icon>
              <v-icon>exit_to_app</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Выход</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list-item-group>
      </v-list>
      <v-divider />
      <v-list dense>
        <v-list-item disabled>
          <v-list-item-icon>
            <v-icon>info</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Version</v-list-item-title>
            <v-list-item-subtitle>{{ appVersion || "—" }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>

<script>
import navigationFilter from "@/filters/navigation.filter";
import dateIsoFilter from "@/filters/dateIso.filter";
import fetch from "@/utils/fetch";
import vm2js from "@/utils/vm2js";
import jwtParser from "@/utils/jwtParser";
import ctx from "@/ctx";
import storage from "../../utils/storage";

function createPaidFilter() {
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return {
    from: dateIsoFilter(from),
    to: dateIsoFilter(new Date()),
    hospitalIds: []
  };
}

function hasAccess(accessName) {
  const jwt = storage.getJwt();
  const parsed = jwtParser.parse(jwt);
  if (Array.isArray(parsed?.access)) {
    const entry = parsed.access.find(a => a && a.access === accessName);
    if (entry && entry.rights && entry.rights !== "---" && entry.rights.includes("r")) {
      return true;
    }
  }

  const raw = localStorage.getItem("userAccess") || "";
  return raw.split(",").some(entry => {
    const [access, rights] = entry.split(":");
    return access === accessName && rights && rights !== "---" && rights.includes("r");
  });
}

export default {
  data: () => ({
    storage,
    groups: [],
    appVersion: "",
    paidDialog: false,
    paidFilter: createPaidFilter(),
    hospitals: [],
    hospitalsLoading: false,
    exporting: false,
    syncingCatalogs: false,
    syncingServices: false
  }),
  computed: {
    hasPaidServiceAccess() {
      return hasAccess("PAID_SERVICE");
    },
    canExport() {
      return !!(this.paidFilter.from && this.paidFilter.to);
    }
  },
  methods: {
    profile() {
      this.$router.push("/profile");
    },
    syncUserAccessFromJwt() {
      const parsed = jwtParser.parse(storage.getJwt());
      if (!Array.isArray(parsed?.access)) return;
      storage.setItem(
        "userAccess",
        parsed.access.map(a => `${a.access}:${a.rights}`)
      );
    },
    openPaidDialog() {
      if (!this.hasPaidServiceAccess) return;
      this.paidFilter = createPaidFilter();
      this.paidDialog = true;
      this.fetchHospitals();
    },
    closePaidDialog() {
      this.paidDialog = false;
    },
    async fetchHospitals() {
      this.hospitalsLoading = true;
      try {
        const list = await fetch.get("/api/mis/hospitals/get-list");
        this.hospitals = Array.isArray(list) ? list : [];
      } finally {
        this.hospitalsLoading = false;
      }
    },
    async syncMisCatalogs() {
      if (!this.hasPaidServiceAccess) return;
      this.syncingCatalogs = true;
      try {
        const result = await fetch.post("/api/mis/catalogs/sync", null);
        if (result) {
          const total =
            (result.total != null
              ? result.total
              : (result.services || 0) +
                (result.employees || 0) +
                (result.departments || 0) +
                (result.organizations || 0) +
                (result.juridicals || 0)) || 0;
          ctx.system.msg = `Справочники MIS обновлены (${total})`;
          await this.fetchHospitals();
        }
      } finally {
        this.syncingCatalogs = false;
      }
    },
    async syncMisServices() {
      if (!this.hasPaidServiceAccess) return;
      this.syncingServices = true;
      try {
        const result = await fetch.post(
          "/api/mis/catalogs/visits/sync",
          vm2js({
            hospitalIds: this.paidFilter.hospitalIds || []
          })
        );
        if (result != null) {
          const count =
            typeof result === "number"
              ? result
              : result.visits != null
              ? result.visits
              : 0;
          ctx.system.msg = `Услуги MIS обновлены (${count})`;
        }
      } finally {
        this.syncingServices = false;
      }
    },
    async exportPaidReport() {
      if (!this.canExport || !this.hasPaidServiceAccess) return;
      this.exporting = true;
      try {
        const res = await fetch.post(
          "/api/mis/report/export",
          vm2js(this.paidFilter)
        );
        if (res && res.blob) {
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(await res.blob);
          link.download = res.filename || "report-mis.xlsx";
          link.click();
        }
      } finally {
        this.exporting = false;
      }
    },
    async loadAppVersion() {
      try {
        const res = await fetch.get("/api/app/version");
        this.appVersion = res && res.version ? res.version : "";
      } catch (e) {
        this.appVersion = "";
      }
    }
  },
  mounted() {
    this.syncUserAccessFromJwt();
    this.groups = navigationFilter(this.$router.options.routes);
    this.loadAppVersion();
  }
};
</script>

<style lang="scss" scoped>
@media only screen and (max-width: 959px) {
  .navbar-menu {
    display: none;
  }
}
.navbar {
  display: flex;
  flex-grow: 1;
  margin-left: 2rem;
}
.paid-sync-actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.5rem;
}
.md-button-text {
  margin-left: 0.5rem;
}
</style>
